<script setup lang="ts">
/**
 * DrawerHeader — canonical drawer header chrome.
 *
 * Three slot positions, all optional except the title:
 *   1. `#avatar` (left) — Avatar or other lead glyph
 *   2. `title` + `subtitle` props (center) — text identity block
 *   3. `chipLabel` + `chipColor` props or `#extras` slot — inline chip /
 *      badges next to the title
 *   4. Built-in close X (right) — fires `close` event
 *
 * Use inside a Drawer's `#header` slot. The kit's Drawer hides its
 * built-in X via `:show-close-icon="false"` and this helper supplies
 * the explicit close affordance. Avoids the "two Xs in the header"
 * smell the audit flagged across multiple drawers.
 *
 * For headers with rich inline content (multi-line subtitle structures,
 * inline pipeline indicators, etc.), use a custom `#header` slot
 * directly — DrawerHeader covers the 80% case.
 */
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Chip from '@uiKit/elements/Chip.vue'
import type { ChipColor } from '@uiKit/elements/Chip.vue'

interface Props {
  title: string
  /** Optional muted subtitle / context line below the title. */
  subtitle?: string
  /** Optional status chip rendered next to the title. */
  chipLabel?: string
  chipColor?: ChipColor
  /** Disables the close button — e.g. while a save is in-flight. */
  disableClose?: boolean
}

withDefaults(defineProps<Props>(), {
  chipColor: 'primary',
  disableClose: false,
})

const emit = defineEmits<{
  close: []
}>()
</script>

<template>
  <Box flex align="center" justify="between" gap="3" class="drawer-header">
    <Box flex align="center" gap="3" class="drawer-header__title-group">
      <slot name="avatar" />
      <Box flex direction="col" gap="0" class="drawer-header__text">
        <slot name="title">
          <Text type="page-title" truncate>{{ title }}</Text>
        </slot>
        <slot name="subtitle">
          <Text v-if="subtitle" type="caption" color="muted">{{ subtitle }}</Text>
        </slot>
      </Box>
      <Chip v-if="chipLabel" :label="chipLabel" :color="chipColor" variant="outline" size="small" />
      <slot name="extras" />
    </Box>
    <Button
      variant="text"
      color="secondary"
      size="medium"
      icon="times"
      aria-label="Close"
      :disabled="disableClose"
      @click="emit('close')"
    />
  </Box>
</template>

<style scoped>
.drawer-header {
  width: 100%;
}

.drawer-header__title-group {
  min-width: 0;
  flex: 1;
}

.drawer-header__text {
  min-width: 0;
  flex: 1;
}
</style>
